import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/ui-bits";
import { CLINICIANS } from "@/data/team";

const ROLES = [
  {
    title: "Consultant endocrinologists",
    copy: "GMC-registered specialists in endocrinology and diabetes, responsible for complex cases, blood result review and the oversight of treatment decisions.",
  },
  {
    title: "Specialist GPs in men's health",
    copy: "GMC-registered doctors with postgraduate training in men's health, conducting assessments, consultations and ongoing reviews.",
  },
  {
    title: "Clinical governance lead",
    copy: "Owns our assessment protocols, monitoring schedules, escalation pathways and complaints process — and signs off every page of public-facing clinical content.",
  },
  {
    title: "Nursing & phlebotomy partners",
    copy: "Registered nurses and trained phlebotomists for venous sample collection, working to documented clinical standards.",
  },
];

const STANDARDS = [
  {
    h: "Named accountability",
    p: "Full clinician profiles — names, GMC numbers, registration status and clinical responsibilities — are published on this page at launch, alongside the date each section of content was last medically reviewed.",
  },
  {
    h: "Regulatory positioning",
    p: "We will describe our legal entity, CQC registration and scope of regulated activities accurately — and will not imply that a registration held elsewhere covers this service. Registration details are published here before launch.",
  },
  {
    h: "Editorial standards",
    p: "Every clinical page carries an author, a review date and a clear distinction between screening, diagnosis and treatment. No fake biographies, no invented statistics, no guaranteed outcomes.",
  },
  {
    h: "Complaints, taken seriously",
    p: "A clear complaints route is published on our contact page, handled separately from clinical care, with concerns about safety escalated through documented clinical governance procedures.",
  },
];

export default function ClinicalTeam() {
  return (
    <>
      <Seo
        title="Clinical Team & Governance | Androgen Health"
        description="Meet the Androgen Health TRT specialists — Dr Venkata Bandaru (GMC 6141015), Shamir Patel (GPhC 2049338) and Hamza Khairkhwah (GPhC 2228294) — and the clinical governance behind the service."
      />
      <PageHero
        label="Clinical team & governance"
        title="Specialists first. Marketers not at all."
        lede="Androgen Health is built as a clinical service with governance designed in from day one — not a marketing site with a doctor attached."
        safety={false}
      />

      <section className="pb-24 px-5 sm:px-8" data-testid="team-body">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="card-line rounded-2xl p-6 sm:p-8 border-l-2 !border-l-teal" data-testid="team-launch-note">
              <Label>Named, registered, accountable</Label>
              <p className="text-mute text-sm sm:text-base leading-relaxed mt-4">
                Every clinician below is named with their regulator and registration number so you
                can verify them on the public register. CQC registration details and content review
                dates are added to this page as the service opens.
              </p>
            </div>
          </Reveal>

          <div className="mt-14">
            <Reveal>
              <Label>TRT specialists</Label>
            </Reveal>
            <div className="grid lg:grid-cols-3 gap-5 mt-6" data-testid="team-clinicians">
              {CLINICIANS.map((c, i) => (
                <Reveal key={c.slug} delay={i * 0.08} className="h-full">
                  <article className="card-line rounded-2xl overflow-hidden h-full flex flex-col" data-testid={`clinician-${c.slug}`}>
                    <div className="relative aspect-[4/4] overflow-hidden">
                      <img src={c.image} alt={`Portrait of ${c.name}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h2 className="font-display text-3xl tracking-wide">{c.name}</h2>
                      <p className="text-teal text-sm mt-1">{c.role}</p>
                      <p className="text-mute text-sm leading-relaxed mt-4">{c.summary}</p>
                      <details className="mt-4 group">
                        <summary className="cursor-pointer text-sm text-bone/80 hover:text-teal transition-colors list-none inline-flex items-center gap-2" data-testid={`clinician-${c.slug}-more`}>
                          <span className="group-open:hidden">Full profile</span>
                          <span className="hidden group-open:inline">Hide profile</span>
                          <span className="text-teal">+</span>
                        </summary>
                        <div className="mt-4 space-y-4 text-sm text-mute leading-relaxed">
                          <p>{c.experience}</p>
                          <ul className="space-y-1.5">
                            {c.specialisms.map((s) => (
                              <li key={s} className="flex gap-2 text-bone/80"><span className="text-teal">·</span>{s}</li>
                            ))}
                          </ul>
                          <p>{c.education}</p>
                        </div>
                      </details>
                      <dl className="mt-auto pt-5 border-t border-line grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-xs">
                        <dt className="mono-label text-dusk">Regulator</dt>
                        <dd className="text-bone/80">{c.regulator}</dd>
                        <dt className="mono-label text-dusk">Reg. no.</dt>
                        <dd>
                          <a href={c.regUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-teal hover:underline" data-testid={`clinician-${c.slug}-reg`}>{c.regNo}</a>
                        </dd>
                      </dl>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-20">
            <Reveal>
              <Label>Wider clinical model</Label>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            {ROLES.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.07} className="h-full">
                <div className="card-line rounded-2xl p-8 h-full">
                  <h2 className="font-display text-2xl sm:text-3xl tracking-wide">{r.title}</h2>
                  <p className="text-mute text-sm leading-relaxed mt-3">{r.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-20 max-w-3xl space-y-12">
            {STANDARDS.map((s, i) => (
              <Reveal key={s.h} delay={i * 0.04}>
                <article>
                  <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-wide">{s.h}</h2>
                  <p className="text-mute text-base leading-relaxed mt-4">{s.p}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-20 card-line rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">Questions before launch?</h2>
              <p className="text-mute mt-4 max-w-xl mx-auto leading-relaxed">
                Governance questions are welcome — use the contact route and mark your enquiry
                "clinical governance".
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
